import { definedMap } from "@glideapps/ts-necessities";
import type { DocumentData, LatLng } from "../Database";
import { documentIDColumnName } from "../Database";
import type { TableName } from "../database-strings";
import { makeCommentsPath } from "../database-strings";
import { QuotaKind } from "../Database/quotas";
import { logError } from "../debug-print";
import { defaultCurrency, findCurrency, formatPrice } from "./buy-button-defaults";
import { makeTableName } from "../description";
import { assert, checkString, defined, definedMapWithDefault, isArray, isOlderThan } from "../Support";
import type { YesCodeValue } from "../yes-code-types";
import type { MaybeForwardee } from "./component-helpers";
import {
    asArrayValue,
    asBasePrimitiveValue,
    asBoolean,
    asMaybeString,
    asNumber,
    asString,
    AsyncRecomputingValueProducer,
    evaluateStringArray,
    FetchingValueProducer,
    ForwardingFromOneOrTwoValueProducer,
    ForwardingFromTwoValueProducer,
    ForwardingValueProducer,
    ImmutableSubscribableValueProducer,
    isLoadedDefined,
    loadedDefinedMap,
    makeObjectInspector,
    RecomputingFromOneOrTwoValueProducer,
    RecomputingFromTwoValueProducer,
} from "./component-helpers";
import type { Loading } from "./computation-types";
import { Callable, isObjectValue, LOADING } from "./computation-types";
import { Constant, Data, makeValueFromJSON } from "./data";
import { registerBuiltinFunction } from "./functions";
import { geocodeAddress } from "./geocode";
import { BuiltinCallable } from "./primitives";
import type {
    AppEnvironment,
    AppOther,
    Context,
    DataStore,
    EnvironmentBoundEvaluable,
    ForceValueProducer,
    ObjectInspector,
    PotentialValue,
    RootEvalEnvironment,
    Value,
    ValueProducer,
} from "./types";
import { convertJSONObjectToRowData } from "./datastore/row-data";

const latLongPromises: Map<string, Promise<Value>> = new Map();
class LatLngValueProducer extends FetchingValueProducer<AppOther, AppEnvironment, string> {
    constructor(
        private readonly _appEnvironment: AppEnvironment,
        public readonly address: string,
        private readonly _key: string | undefined
    ) {
        super(latLongPromises, address);
    }

    private async fetchAsync(promise: Promise<LatLng | undefined>): Promise<Value> {
        const result = await promise;
        if (result === undefined) return undefined;
        return makeValueFromJSON<AppOther, AppEnvironment>({ latitude: result.lat, longitude: result.lng }, true);
    }

    protected fetch(address: string): Value | Promise<Value> {
        const result = geocodeAddress(this._appEnvironment, address, this._key);
        if (result === undefined) return undefined;
        if (result instanceof Promise) {
            return this.fetchAsync(result);
        }
        return makeValueFromJSON<AppOther, AppEnvironment>({ latitude: result.lat, longitude: result.lng }, true);
    }

    public getObjectInspector(force: ForceValueProducer | undefined): ObjectInspector | Loading | undefined {
        return makeObjectInspector(this, force);
    }
}

class LookupGeoAddressValueProducer extends ForwardingFromOneOrTwoValueProducer<AppOther, AppEnvironment> {
    constructor(private readonly _appEnvironment: AppEnvironment, value: ValueProducer, quotaKey: ValueProducer) {
        super(_appEnvironment, value, quotaKey);
    }

    protected recomputeForwardeeFromInputs(
        addressValue: PotentialValue,
        quotaKeyValue: PotentialValue,
        currentForwardee: MaybeForwardee<AppOther, AppEnvironment>
    ): ValueProducer | Loading | undefined {
        if (addressValue === LOADING || addressValue === undefined) return addressValue;
        const address = asString(addressValue);

        if (currentForwardee instanceof LatLngValueProducer) {
            if (currentForwardee.address === address) {
                return currentForwardee;
            }
        }
        return new LatLngValueProducer(this._appEnvironment, address, loadedDefinedMap(quotaKeyValue, asString));
    }
}

let mockedOldComputationModelCurrentLocation: LatLng | undefined;
export function mockOldComputationCurrentLocation(value: LatLng | undefined): void {
    mockedOldComputationModelCurrentLocation = value;
}

export interface CurrentLocationListener {
    updateLocation(latitude: number, longitude: number): void;
    locationError(): void;
}

const locationListeners: Set<CurrentLocationListener> = new Set();
let watchID: number | undefined;
// We will immediately return the last position if it's not too old
let lastPosition: { p: GeolocationPosition; d: Date } | undefined;

function onWatchSuccess(position: GeolocationPosition): void {
    // logInfo("got geolocation", position);

    lastPosition = { p: position, d: new Date() };
    for (const l of locationListeners) {
        try {
            l.updateLocation(position.coords.latitude, position.coords.longitude);
        } catch (e: unknown) {
            logError("Error calling position update", e);
        }
    }
}

function onWatchError(error: GeolocationPositionError): void {
    logError("geolocation error", error);

    for (const l of locationListeners) {
        try {
            l.locationError();
        } catch (e: unknown) {
            logError("Error calling position error handler", e);
        }
    }
}

export function listenToCurrentLocation(listener: CurrentLocationListener): void {
    locationListeners.add(listener);
    if (watchID === undefined) {
        try {
            watchID = navigator.geolocation.watchPosition(onWatchSuccess, onWatchError);
            // logInfo("listening to geolocation", watchID);
        } catch (e: unknown) {
            logError("Cannot watch geolocation", e);
            listener.locationError();
        }
    }
}

export function stopListeningToCurrentLocation(listener: CurrentLocationListener): void {
    locationListeners.delete(listener);
    if (locationListeners.size === 0 && watchID !== undefined) {
        try {
            // logInfo("stopping listening to geolocation", watchID);
            navigator.geolocation.clearWatch(watchID);
            watchID = undefined;
        } catch (e: unknown) {
            logError("Error unwatching location", e);
            watchID = undefined;
        }
    }
}

class CurrentLocationValueProducer
    extends ImmutableSubscribableValueProducer<AppOther, AppEnvironment>
    implements CurrentLocationListener
{
    private _position: { latitude: ValueProducer; longitude: ValueProducer } | Loading | undefined = LOADING;

    private getPositionIfPossible(): void {
        if (mockedOldComputationModelCurrentLocation !== undefined) {
            this._position = {
                latitude: new Data<AppOther, AppEnvironment>(mockedOldComputationModelCurrentLocation.lat),
                longitude: new Data<AppOther, AppEnvironment>(mockedOldComputationModelCurrentLocation.lng),
            };
            return;
        }

        if (lastPosition === undefined) return;
        if (isOlderThan(lastPosition.d, 60 * 1000)) {
            lastPosition = undefined;
            return;
        }

        if (isLoadedDefined(this._position)) {
            this._position.latitude.set(lastPosition.p.coords.latitude);
            this._position.longitude.set(lastPosition.p.coords.longitude);
        } else {
            this._position = {
                latitude: new Data<AppOther, AppEnvironment>(lastPosition.p.coords.latitude),
                longitude: new Data<AppOther, AppEnvironment>(lastPosition.p.coords.longitude),
            };
        }
    }

    public get current(): PotentialValue {
        this.getPositionIfPossible();
        return this._position;
    }

    public firstSubscribed(): void {
        super.firstSubscribed();
        listenToCurrentLocation(this);
    }

    public lastUnsubscribed(): void {
        super.lastUnsubscribed();
        stopListeningToCurrentLocation(this);
    }

    public updateLocation(): void {
        this.setListenersDirty();
    }

    public locationError(): void {
        if (this._position === LOADING) {
            this._position = undefined;
            this.setListenersDirty();
        }
    }

    public getObjectInspector(force: ForceValueProducer | undefined): ObjectInspector | Loading | undefined {
        return makeObjectInspector(this, force);
    }
}

class FetchCommentsValueProducer extends RecomputingFromOneOrTwoValueProducer<AppOther, AppEnvironment> {
    private _currentTopic: string | undefined;
    private _unlisten: (() => void) | undefined;

    constructor(
        private readonly _appEnvironment: AppEnvironment,
        topic: ValueProducer,
        sortOrder: ValueProducer | undefined
    ) {
        super(_appEnvironment, topic, sortOrder);
    }

    private unlisten(): void {
        if (this._unlisten === undefined) return;

        this._unlisten();
        this._unlisten = undefined;
    }

    protected lastUnsubscribed(): void {
        super.lastUnsubscribed();
        this.unlisten();
    }

    protected recomputeFromInputs(topicValue: Value, sortOrderValue: Value): PotentialValue {
        if (topicValue === undefined) return undefined;

        const topic = asString(topicValue);
        if (this._currentTopic === topic) return this.currentValue;

        const sortOrder = definedMapWithDefault(sortOrderValue, "asc", asString) as "asc" | "desc";

        const db = this._appEnvironment.database;

        this.unlisten();

        this._currentTopic = topic;
        this._unlisten = db.listenWhere(
            makeCommentsPath(this._appEnvironment.appID),
            [{ fieldPath: "topic", opString: "==", value: asString(topic) }],
            "dateTime",
            "asc",
            undefined,
            results => {
                const rows = results.map(({ data, id }) => {
                    const row: DocumentData = { ...data, dateTime: db.convertDocumentProperty(data.dateTime) };
                    row[documentIDColumnName] = id;
                    return row;
                });
                // Firestore will never call the callback if we use `desc` source order and
                // there are no documents, so we just reverse it ourselves.
                if (sortOrder === "desc") {
                    rows.reverse();
                }
                this.currentValue = makeValueFromJSON<AppOther, AppEnvironment>(rows, false);
                this.setListenersDirty();
            }
        );

        return LOADING;
    }
}

class FetchCommentsCallable extends Callable<AppOther, AppEnvironment> {
    public call(args: EnvironmentBoundEvaluable[], context: Context, rootEnv: RootEvalEnvironment): ValueProducer {
        return new FetchCommentsValueProducer(
            rootEnv.appEnvironment,
            args[0].eval(context),
            definedMap(args[1], a => a.eval(context))
        );
    }
}

class CurrentPivotValueProducer extends ImmutableSubscribableValueProducer<AppOther, AppEnvironment> {
    constructor(private readonly _appEnvironment: AppEnvironment) {
        super();
    }

    public get current(): Value {
        return this._appEnvironment.currentPivot;
    }

    public getObjectInspector(): ObjectInspector | Loading | undefined {
        return undefined;
    }
}

abstract class AuthenticatorDataValueProducer extends ImmutableSubscribableValueProducer<AppOther, AppEnvironment> {
    constructor(protected readonly _appEnvironment: AppEnvironment) {
        super();
    }

    private emailChanged = () => {
        this.setListenersDirty();
    };

    protected firstSubscribed(): void {
        super.firstSubscribed();

        this._appEnvironment.authenticator.addCallback(this.emailChanged);
    }

    protected lastUnsubscribed(): void {
        super.lastUnsubscribed();

        this._appEnvironment.authenticator.removeCallback(this.emailChanged);
    }
}

export class VerifiedEmailAddressValueProducer extends AuthenticatorDataValueProducer {
    public get current(): PotentialValue {
        return this._appEnvironment.authenticator.virtualEmail;
    }

    public getObjectInspector(): ObjectInspector | Loading | undefined {
        return undefined;
    }
}

export class RealEmailAddressValueProducer extends AuthenticatorDataValueProducer {
    public get current(): PotentialValue {
        return this._appEnvironment.authenticator.realEmail;
    }

    public getObjectInspector(): ObjectInspector | Loading | undefined {
        return undefined;
    }
}

class HaveUserSpecificDataValueProducer extends AuthenticatorDataValueProducer {
    public get current(): PotentialValue {
        return this._appEnvironment.authenticator.realEmail !== undefined;
    }

    public getObjectInspector(): ObjectInspector | Loading | undefined {
        return undefined;
    }
}

class FallbackUsernameValueProducer extends ForwardingValueProducer<AppOther, AppEnvironment> {
    constructor(private readonly _appEnvironment: AppEnvironment) {
        super();
    }

    protected recomputeForwardee(): MaybeForwardee<AppOther, AppEnvironment> {
        return this._appEnvironment.fallbackUsername;
    }

    public getObjectInspector(): ObjectInspector | Loading | undefined {
        return undefined;
    }
}

export class FetchTableRowsValueProducer extends ForwardingFromTwoValueProducer<AppOther, AppEnvironment> {
    constructor(
        private readonly _appEnvironment: AppEnvironment,
        dataStoreName: ValueProducer,
        tableName: ValueProducer,
        private readonly _forBuilder: boolean
    ) {
        super(_appEnvironment, dataStoreName, tableName);
    }

    protected recomputeForwardeeFromInputs(
        dataStoreName: PotentialValue,
        tableName: PotentialValue
    ): ValueProducer | Loading | undefined {
        if (tableName === undefined || tableName === LOADING) return tableName;

        let dataStore: DataStore;
        if (loadedDefinedMap(dataStoreName, asString) === "local") {
            dataStore = this._appEnvironment.localDataStore;
        } else {
            dataStore = this._appEnvironment.dataStore;
        }

        return dataStore.fetchTableRows(makeTableName(asString(tableName)), this._forBuilder);
    }
}

class FetchTableRowsCallable extends Callable<AppOther, AppEnvironment> {
    public call(args: EnvironmentBoundEvaluable[], context: Context, rootEnv: RootEvalEnvironment): ValueProducer {
        const dataStoreName = args[0].eval(context);
        const tableName = args[1].eval(context);
        const forBuilder = loadedDefinedMap(args[2]?.eval(context)?.current, asBoolean) ?? false;
        return new FetchTableRowsValueProducer(rootEnv.appEnvironment, dataStoreName, tableName, forBuilder);
    }
}

class FetchSingleLinkRowCallable extends Callable<AppOther, AppEnvironment> {
    public call(args: EnvironmentBoundEvaluable[], context: Context, rootEnv: RootEvalEnvironment): ValueProducer {
        const { dataStore } = rootEnv.appEnvironment;
        const link = args[0].eval(context);
        const forBuilder = loadedDefinedMap(args[1]?.eval(context)?.current, asBoolean) ?? false;
        return dataStore.fetchSingleLinkRow(link, forBuilder);
    }
}

class FetchMultipleLinkRowsCallable extends Callable<AppOther, AppEnvironment> {
    public call(args: EnvironmentBoundEvaluable[], context: Context, rootEnv: RootEvalEnvironment): ValueProducer {
        const { dataStore } = rootEnv.appEnvironment;
        const link = args[0].eval(context);
        const forBuilder = loadedDefinedMap(args[1]?.eval(context)?.current, asBoolean) ?? false;
        return dataStore.fetchMultipleLinkRows(link, forBuilder);
    }
}

class IsRowQuotaReachedCallable extends Callable<AppOther, AppEnvironment> {
    public call(_args: EnvironmentBoundEvaluable[], _context: Context, rootEnv: RootEvalEnvironment): ValueProducer {
        return rootEnv.appEnvironment.getQuotaReachedFlag(QuotaKind.RowsUsed);
    }
}

class IsGeolocationQuotaReachedCallable extends Callable<AppOther, AppEnvironment> {
    public call(_args: EnvironmentBoundEvaluable[], _context: Context, rootEnv: RootEvalEnvironment): ValueProducer {
        return rootEnv.appEnvironment.getGeocodeQuotaReachedFlag();
    }
}

class FormatPriceValueProducer extends RecomputingFromTwoValueProducer<AppOther, AppEnvironment> {
    protected recomputeFromInputs(priceValue: Value, currencySymbolValue: Value): PotentialValue {
        if (priceValue === undefined) return undefined;

        const price = asNumber(priceValue);
        const currencyCode = definedMapWithDefault(currencySymbolValue, defaultCurrency.code, asString);
        const currency = findCurrency(currencyCode);
        if (currency === undefined) {
            logError("Currency not found", currencyCode);
            return undefined;
        }

        return formatPrice(price, currency);
    }
}

class AddComputedColumnsToDataCallable extends Callable<AppOther, AppEnvironment> {
    public call(args: EnvironmentBoundEvaluable[], context: Context, rootEnv: RootEvalEnvironment): ValueProducer {
        const dataVP = args[1].eval(context);
        const data = dataVP.current;
        if (!isObjectValue(data)) return dataVP;

        const tableName = args[0].eval(context).current;
        if (!isLoadedDefined(tableName)) return dataVP;

        const newData = new Data({ ...data });
        rootEnv.appEnvironment.dataStore.addComputedColumnsToData(makeTableName(checkString(tableName)), newData);
        return newData;
    }
}

class AddValueProducersToDataCallable extends Callable<AppOther, AppEnvironment> {
    public call(args: EnvironmentBoundEvaluable[], context: Context): ValueProducer {
        const lhsVP = args[0].eval(context);
        const lhs = lhsVP.current;
        const rhs = args[1].eval(context).current;
        const columnNamesSet = evaluateStringArray(args[2], context, undefined);

        if (!isObjectValue(lhs) || !isObjectValue(rhs) || !isLoadedDefined(columnNamesSet)) return lhsVP;

        const newObject = { ...lhs };
        for (const k of columnNamesSet) {
            const vp = rhs[k];
            if (vp === undefined) continue;

            newObject[k] = vp;
        }

        return new Data(newObject);
    }
}

export class WithColumnFormatInBuilderValueProducer extends ForwardingValueProducer<AppOther, AppEnvironment> {
    private _valueWithFormat: ValueProducer | undefined | Loading = LOADING;
    private readonly _tableName: TableName;
    private readonly _columnName: string;

    constructor(
        private readonly _appEnvironment: AppEnvironment,
        _dataStoreName: ValueProducer,
        tableNameVP: ValueProducer,
        columnNameVP: ValueProducer,
        private readonly _value: ValueProducer
    ) {
        super();

        this._tableName = makeTableName(defined(loadedDefinedMap(tableNameVP.current, asString)));
        this._columnName = defined(loadedDefinedMap(columnNameVP.current, asString));
    }

    protected firstSubscribed(): void {
        super.firstSubscribed();

        assert(this._valueWithFormat === LOADING);

        this._appEnvironment.registerColumnFormatChangedCallback(
            this._tableName,
            this._columnName,
            this.columnFormatChanged
        );
    }

    protected lastUnsubscribed(): void {
        super.lastUnsubscribed();

        this._valueWithFormat = LOADING;

        this._appEnvironment.unregisterColumnFormatChangedCallback(
            this._tableName,
            this._columnName,
            this.columnFormatChanged
        );
    }

    protected recomputeForwardee(): MaybeForwardee<AppOther, AppEnvironment> {
        let valueWithFormat = this._valueWithFormat;

        if (valueWithFormat === LOADING) {
            const evaluable = this._appEnvironment.getColumnFormat(this._tableName, this._columnName);
            valueWithFormat = definedMap(evaluable, e => e.eval(this._value));

            if (this.hasSubscribers) {
                this._valueWithFormat = valueWithFormat;
            }
        }

        if (valueWithFormat !== undefined) {
            return valueWithFormat;
        } else {
            return this._value;
        }
    }

    protected columnFormatChanged = (): void => {
        this._valueWithFormat = LOADING;

        this.setDirty(this);
    };
}

export class LoginUserProfileRowValueProducer extends ForwardingValueProducer<AppOther, AppEnvironment> {
    constructor(private readonly _appEnvironment: AppEnvironment) {
        super();
    }

    protected firstSubscribed(): void {
        super.firstSubscribed();
        this._appEnvironment.authenticator.addCallback(this.appUserChanged);
    }

    protected lastUnsubscribed(): void {
        super.lastUnsubscribed();
        this._appEnvironment.authenticator.removeCallback(this.appUserChanged);
    }

    private readonly appUserChanged = () => {
        this.setDirty(this);
    };

    protected recomputeForwardee(): MaybeForwardee<AppOther, AppEnvironment> {
        const { database } = this._appEnvironment;

        const { userProfileRow } = this._appEnvironment.authenticator;
        if (userProfileRow === undefined) return undefined;

        const rowData = convertJSONObjectToRowData(database, userProfileRow);

        const userProfile = this._appEnvironment.userProfileTableInfo;
        if (userProfile?.tableName !== undefined) {
            this._appEnvironment.dataStore.addComputedColumnsToData(userProfile.tableName, rowData);
        }

        return rowData;
    }
}

class CurrentURLValueProducer extends ImmutableSubscribableValueProducer<AppOther, AppEnvironment> {
    public get current(): Value {
        return window.location.href;
    }

    public getObjectInspector(): ObjectInspector | Loading | undefined {
        return undefined;
    }
}

class YesCodeFetchValueProducer extends AsyncRecomputingValueProducer<AppOther, AppEnvironment> {
    private _subscribed = new Set<ValueProducer>();

    constructor(
        private readonly _appEnvironment: AppEnvironment,
        private readonly _url: ValueProducer,
        private readonly _params: ValueProducer
    ) {
        super();
    }

    protected firstSubscribed(): void {
        super.firstSubscribed();
        this._url.subscribe(this);
        this._params.subscribe(this);
        assert(this._subscribed.size === 0);
    }

    private unsubscribeParams(): void {
        for (const vp of this._subscribed) {
            vp.unsubscribe(this);
        }
        this._subscribed.clear();
    }

    protected lastUnsubscribed(): void {
        super.lastUnsubscribed();
        this._url.unsubscribe(this);
        this._params.unsubscribe(this);
        this.unsubscribeParams();
    }

    protected recomputeAsync(): PotentialValue | Promise<PotentialValue> {
        const newSubscribed = new Set<ValueProducer>();

        const finish = (v: PotentialValue): PotentialValue => {
            for (const vp of this._subscribed) {
                if (newSubscribed.has(vp)) continue;
                vp.unsubscribe(this);
            }
            this._subscribed = newSubscribed;
            return v;
        };

        const finishResult = (result: YesCodeValue | undefined): PotentialValue => {
            if (isArray(result)) {
                return finish(result.map(v => new Constant<AppOther, AppEnvironment>(v)));
            } else {
                return finish(result);
            }
        };

        if (this._url.current === LOADING || this._params.current === LOADING) return finish(LOADING);

        const url = asMaybeString(this._url.current);
        if (url === undefined) return finish(undefined);

        const paramsArray = definedMap(this._params.current, asArrayValue);
        if (paramsArray === undefined) return finish(undefined);

        const force = (vp: ValueProducer): PotentialValue => {
            if (!this._subscribed.has(vp) && !newSubscribed.has(vp)) {
                vp.subscribe(this);
            }
            newSubscribed.add(vp);
            return vp.current;
        };

        const params = new Map<string, YesCodeValue>();
        for (const keyValueVP of paramsArray) {
            const keyValueValue = force(keyValueVP);
            if (keyValueValue === LOADING) return finish(LOADING);

            const [keyVP, valueVP] = asArrayValue(keyValueValue);
            const keyValue = force(keyVP);
            const valueValue = force(valueVP);
            if (keyValue === LOADING || valueValue === LOADING) return finish(LOADING);

            const key = asMaybeString(keyValue);
            if (key === undefined) continue;

            let value: YesCodeValue | undefined;
            // FIXME: Allow all primitive values, including dates
            if (isArray(valueValue)) {
                value = valueValue.map(vp => loadedDefinedMap(force(vp), asBasePrimitiveValue));
            } else {
                value = asBasePrimitiveValue(valueValue);
            }

            if (value === undefined) continue;

            params.set(key, value);
        }

        const result = this._appEnvironment.appFacilities.callYesCode(url, params);
        if (result instanceof Promise) {
            return result.then(finishResult).catch(() => finish(undefined));
        } else {
            return finishResult(result);
        }
    }

    public getObjectInspector(): undefined {
        return undefined;
    }
}

export function registerGlideFunctions(): void {
    registerBuiltinFunction("LookupGeoAddress", new BuiltinCallable(LookupGeoAddressValueProducer));
    registerBuiltinFunction("CurrentLocation", new BuiltinCallable(CurrentLocationValueProducer));
    registerBuiltinFunction("FetchComments", new FetchCommentsCallable());
    registerBuiltinFunction("CurrentPivot", new BuiltinCallable(CurrentPivotValueProducer));
    registerBuiltinFunction("VerifiedEmailAddress", new BuiltinCallable(VerifiedEmailAddressValueProducer));
    registerBuiltinFunction("RealEmailAddress", new BuiltinCallable(RealEmailAddressValueProducer));
    registerBuiltinFunction("FallbackUsername", new BuiltinCallable(FallbackUsernameValueProducer));
    registerBuiltinFunction("HaveUserSpecificData", new BuiltinCallable(HaveUserSpecificDataValueProducer));
    registerBuiltinFunction("FetchTableRows", new FetchTableRowsCallable());
    registerBuiltinFunction("FetchSingleLinkRow", new FetchSingleLinkRowCallable());
    registerBuiltinFunction("FetchMultipleLinkRows", new FetchMultipleLinkRowsCallable());
    registerBuiltinFunction("IsRowQuotaReached", new IsRowQuotaReachedCallable());
    registerBuiltinFunction("IsGeolocationQuotaReached", new IsGeolocationQuotaReachedCallable());
    registerBuiltinFunction("FormatPrice", new BuiltinCallable(FormatPriceValueProducer));
    registerBuiltinFunction("AddComputedColumnsToData", new AddComputedColumnsToDataCallable());
    registerBuiltinFunction("AddValueProducersToData", new AddValueProducersToDataCallable());
    registerBuiltinFunction("WithColumnFormatInBuilder", new BuiltinCallable(WithColumnFormatInBuilderValueProducer));
    registerBuiltinFunction("LoginUserProfileRow", new BuiltinCallable(LoginUserProfileRowValueProducer));
    registerBuiltinFunction("CurrentURL", new BuiltinCallable(CurrentURLValueProducer));
    registerBuiltinFunction("YesCodeFetch", new BuiltinCallable(YesCodeFetchValueProducer));
}
